/**
 * 데이터 구동 URL(관리자가 설정·저장해 공유하는 URL) 이동 유틸 모듈
 * @module browser/navigation
 */

interface OpenSafeUrlOptions {
  /** 새 창(탭)으로 열기 */
  newWindow?: boolean;
  /** 팝업 창 크기. 지정하면 새 창으로 열린다 */
  popupSize?: { width: number; height: number };
}

/**
 * origin 기준으로 해석한 스킴이 http/https 인 URL 만 허용한다(`javascript:`·`data:` 등 차단).
 * 스킴만 검사하므로 외부 http/https 호스트 이동은 허용된다 — 커스텀 링크 위젯이 외부 링크를 지원한다.
 *
 * @example
 * isSafeNavigationUrl('/dashboard/1'); // true (origin 스킴으로 해석)
 * isSafeNavigationUrl('javascript:alert(1)'); // false
 */
export const isSafeNavigationUrl = (url: unknown): url is string => {
  if (typeof url !== 'string' || url.trim() === '') {
    return false;
  }
  try {
    const { protocol } = new URL(url.trim(), window.location.origin);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * origin 이 현재 페이지와 같은지 판정한다. 비 http(s) 스킴·파싱 불가 URL 은 `isSafeNavigationUrl` 이
 * 먼저 걸러내므로 여기서는 origin 비교만 한다.
 */
const isSameOriginUrl = (url: string): boolean => {
  try {
    return new URL(url, window.location.origin).origin === window.location.origin;
  } catch {
    return false;
  }
};

/**
 * 스킴 검사를 통과한 URL 로만 이동한다(Stored XSS 방어). 외부 origin 을 새 창으로 열 때는
 * `noopener,noreferrer` 로 reverse tabnabbing 을 막고, 현재 창 이동은 history 오염을 막기 위해
 * `location.replace` 를 쓴다.
 *
 * same-origin 새 창에는 opener 를 끊지 않는다 — 인증 토큰이 sessionStorage 에 있어(auth store),
 * `noopener` 로 열면 브라우저가 opener 의 sessionStorage 를 새 창에 복제하지 않아 내부 화면이
 * 미로그인 상태로 열린다(DSP-44624). reverse tabnabbing 은 외부 origin 에서만 성립하므로
 * same-origin 예외는 방어 수준을 낮추지 않는다.
 *
 * `window.opener` 로 부모와 **통신**하는 내부 팝업(`/popup` 경로)에는 쓰지 않는다 — postMessage
 * 왕복이 필요한 그 용도는 `openBrowserPopup`(`@/common/utils/browserPopupUtils`).
 *
 * @example
 * openSafeUrl(item.url, { newWindow: item.isNewWindow });
 * openSafeUrl(url, { popupSize: { width: 1500, height: 900 } });
 */
export const openSafeUrl = (url: unknown, options: OpenSafeUrlOptions = {}): void => {
  if (!isSafeNavigationUrl(url)) {
    console.error(`[navigation] 허용되지 않은 스킴이라 이동을 취소했습니다: ${String(url)}`);
    return;
  }

  const { newWindow = false, popupSize } = options;
  const targetUrl = url.trim();
  const isSameOrigin = isSameOriginUrl(targetUrl);

  if (popupSize) {
    const sizeFeatures = `width=${popupSize.width},height=${popupSize.height}`;
    window.open(
      targetUrl,
      '_blank',
      isSameOrigin ? sizeFeatures : `${sizeFeatures},noopener,noreferrer`,
    );
  } else if (newWindow) {
    window.open(targetUrl, '_blank', isSameOrigin ? undefined : 'noopener,noreferrer');
  } else {
    window.location.replace(targetUrl);
  }
};
